/*
 * NAGADA components.
 *
 * <nagada-rooms>: walk through the villa one room at a time. Every room is in
 * the page already; this shows one, keeps the list, Back and Next and the strip
 * in step, and puts the room in the address (#room-the-dye-studio) so a room
 * can be shared. The theme editor opens the room whose block is selected.
 */

class NagadaRooms extends HTMLElement {
  connectedCallback() {
    this.rooms = Array.from(this.querySelectorAll('[data-room]'));
    this.links = Array.from(this.querySelectorAll('[data-room-link]'));
    if (this.rooms.length < 2) return;

    this.index = 0;
    this.onClick = this.onClick.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onHashChange = this.onHashChange.bind(this);

    this.addEventListener('click', this.onClick);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    window.addEventListener('hashchange', this.onHashChange);

    const fromHash = this.indexFromHash();
    if (fromHash > -1) this.show(fromHash, { scroll: false });

    // When the rooms open the page, size the photograph so the whole room (photograph, words,
    // Back and Next) fits on the first screen.
    this.fit = () => {
      const media = this.querySelector('.nd-room__media');
      if (!media || window.innerWidth < 750) {
        this.style.removeProperty('--nd-room-fit');
        return;
      }
      this.style.removeProperty('--nd-room-fit');
      const top = media.getBoundingClientRect().top + window.scrollY;
      if (top > window.innerHeight * 0.6) return;
      const room = window.innerHeight - top - 28;
      const ideal = Math.min(760, window.innerWidth * 0.5);
      this.style.setProperty('--nd-room-fit', `${Math.round(Math.max(320, Math.min(ideal, room)))}px`);
    };
    this.onResize = () => {
      cancelAnimationFrame(this.fitFrame);
      this.fitFrame = requestAnimationFrame(this.fit);
    };
    window.addEventListener('resize', this.onResize);
    this.fit();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.fit);

    // Fetch the other rooms' photographs once the page has settled, so Next is instant.
    const warm = () => this.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 3000 });
    else window.setTimeout(warm, 1500);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    window.removeEventListener('hashchange', this.onHashChange);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
  }

  indexFromHash() {
    if (!window.location.hash) return -1;
    const id = decodeURIComponent(window.location.hash.slice(1));
    return this.rooms.findIndex((room) => room.id === id);
  }

  onHashChange() {
    const i = this.indexFromHash();
    if (i > -1 && i !== this.index) this.show(i, { scroll: false });
  }

  onClick(event) {
    const link = event.target.closest('[data-room-link]');
    const step = event.target.closest('[data-room-step]');

    if (link) {
      event.preventDefault();
      this.show(Number(link.dataset.roomLink), { scroll: true, updateUrl: true, announce: true });
    } else if (step) {
      const n = this.rooms.length;
      const direction = step.dataset.roomStep;
      this.show((this.index + Number(direction) + n) % n, { scroll: true, updateUrl: true, announce: true });
      // The button that was pressed is now hidden with its room: keep focus on the same button in the new one.
      this.rooms[this.index].querySelector(`[data-room-step="${direction}"]`)?.focus({ preventScroll: true });
    }
  }

  onBlockSelect(event) {
    const i = this.rooms.findIndex((room) => room.dataset.blockId === event.detail.blockId);
    if (i > -1) this.show(i, { scroll: false });
  }

  show(i, { scroll = false, updateUrl = false, announce = false } = {}) {
    const room = this.rooms[i];
    if (!room) return;
    this.index = i;

    this.rooms.forEach((r, k) => {
      r.hidden = k !== i;
    });
    this.links.forEach((link) => {
      link.setAttribute('aria-current', Number(link.dataset.roomLink) === i ? 'true' : 'false');
    });

    // A strip that scrolls sideways keeps the current photograph's thumbnail in view.
    const strip = this.querySelector('.nd-rooms__thumbs');
    const thumb = strip?.querySelector(`[data-room-link="${i}"]`);
    if (thumb && strip.scrollWidth > strip.clientWidth) {
      const s = strip.getBoundingClientRect();
      const t = thumb.getBoundingClientRect();
      if (t.left < s.left || t.right > s.right) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        strip.scrollBy({ left: t.left - s.left - (s.width - t.width) / 2, behavior: reduce ? 'auto' : 'smooth' });
      }
    }

    if (updateUrl) window.history.replaceState(null, '', `#${room.id}`);

    if (scroll) {
      const top = room.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.5) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        room.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      }
    }

    const status = this.querySelector('[data-room-status]');
    if (announce && status) {
      const position = room.querySelector('[data-room-position]')?.textContent.replace(/\s+/g, ' ').trim() ?? '';
      const name = room.querySelector('.nd-room__name')?.textContent.trim() ?? '';
      status.textContent = `${position}: ${name}`;
    }
  }
}

if (!customElements.get('nagada-rooms')) customElements.define('nagada-rooms', NagadaRooms);

/*
 * NagadaZoom: a closer look at one photograph, inside its own frame. A copy of
 * the photograph is laid over it, scaled and moved; the sharpest version
 * (the frame's data-zoom-src) is fetched the first time it is zoomed.
 * With a mouse, a click zooms in and the photograph follows the pointer; a
 * second click goes closer, to the photograph's actual size, and a third
 * zooms out. On a touch screen, pinch or double-tap, and drag with one finger
 * while zoomed; a single tap calls onTap. Enter steps through the same three
 * clicks from the keyboard, the arrow keys look around and Escape zooms out.
 */

class NagadaZoom {
  constructor(frame, { onTap = null, onDrag = null, onZoom = null } = {}) {
    this.frame = frame;
    this.onTap = onTap;
    this.onDrag = onDrag;
    this.onZoom = onZoom;
    this.layer = null;
    this.level = 0; // clicks so far: 1 is the first zoom, 2 the closer one
    this.scale = 1;
    this.x = 0;
    this.y = 0;

    this.handlers = {
      touchstart: this.onTouchStart.bind(this),
      touchmove: this.onTouchMove.bind(this),
      touchend: this.onTouchEnd.bind(this),
      touchcancel: this.onTouchEnd.bind(this),
      pointerdown: (event) => (this.pointer = event.pointerType),
      pointermove: this.onPointerMove.bind(this),
      click: this.onClick.bind(this),
      keydown: this.onKey.bind(this),
    };
    Object.entries(this.handlers).forEach(([type, handler]) =>
      frame.addEventListener(type, handler, type.startsWith('touch') ? { passive: false } : undefined)
    );
    this.onDocumentKey = (event) => {
      if (event.key === 'Escape') this.reset(true);
    };
  }

  destroy() {
    this.reset(false);
    Object.entries(this.handlers).forEach(([type, handler]) => this.frame.removeEventListener(type, handler));
    clearTimeout(this.tapTimer);
  }

  get zoomed() {
    return Boolean(this.layer);
  }

  // Lay the copy over the photograph, exactly where the photograph sits in the frame.
  start() {
    if (this.layer) return true;
    const base = this.frame.querySelector('img');
    if (!base) return false;
    const w = this.frame.clientWidth;
    const h = this.frame.clientHeight;
    const nw = base.naturalWidth || Number(base.getAttribute('width')) || w;
    const nh = base.naturalHeight || Number(base.getAttribute('height')) || h;
    const style = window.getComputedStyle(base);
    const fit = style.objectFit === 'contain' ? Math.min(w / nw, h / nh) : Math.max(w / nw, h / nh);
    const [px, py] = style.objectPosition.split(' ').map((v) => (v.endsWith('%') ? parseFloat(v) / 100 : 0.5));
    Object.assign(this, { w, h, pw: nw * fit, ph: nh * fit, scale: 1 });
    // The photograph at its actual size. A photograph's own image answers for the page's size
    // of it (it has a srcset), so its full width comes with the frame; a cloth's is the real one.
    this.native = (Number(this.frame.dataset.zoomWidth) || base.naturalWidth || nw) / this.pw;
    this.x = (w - this.pw) * px;
    this.y = (h - this.ph) * (py ?? px);
    this.rest = { x: this.x, y: this.y };

    const layer = document.createElement('img');
    layer.className = 'nd-zoom-layer';
    layer.alt = '';
    layer.decoding = 'async';
    layer.src = this.sharpReady === this.frame.dataset.zoomSrc ? this.sharpReady : base.currentSrc || base.src;
    layer.style.width = `${this.pw}px`;
    layer.style.height = `${this.ph}px`;
    this.layer = layer;
    this.draw();
    this.frame.append(layer);
    void layer.offsetWidth; // so the first move eases in from here
    this.frame.classList.add('is-zoomed');
    this.frame.setAttribute('aria-pressed', 'true');
    if (this.onZoom) this.onZoom(this);
    return true;
  }

  // Zoom far enough to fill the frame, and at least 2.5 times.
  get deep() {
    return Math.max(2.5, this.w / this.pw, this.h / this.ph);
  }

  // The second click: the photograph at its actual size, kept between 1.5 and 2.2 times the first.
  get deeper() {
    const deep = this.deep;
    return Math.min(deep * 2.2, Math.max(deep * 1.5, this.native || 0));
  }

  get max() {
    return Math.max(4, this.deeper);
  }

  // Mark how far in we are: the pointer shows "-" once the next click zooms out.
  setLevel(level) {
    this.level = level;
    this.frame.classList.toggle('is-deepest', level === 2);
  }

  set(scale, x, y) {
    this.scale = Math.min(this.max, Math.max(1, scale));
    const sw = this.pw * this.scale;
    const sh = this.ph * this.scale;
    this.x = sw >= this.w ? Math.min(0, Math.max(this.w - sw, x)) : (this.w - sw) / 2;
    this.y = sh >= this.h ? Math.min(0, Math.max(this.h - sh, y)) : (this.h - sh) / 2;
    this.draw();
  }

  draw() {
    if (this.layer) this.layer.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
  }

  ease() {
    if (!this.layer || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const layer = this.layer;
    layer.classList.add('is-easing');
    clearTimeout(this.easeTimer);
    this.easeTimer = setTimeout(() => layer.classList.remove('is-easing'), 260);
  }

  // Zoom in so that the point p (in the frame) stays where it is.
  zoomAt(p, scale) {
    if (!this.start()) return;
    const cx = (p.x - this.x) / this.scale;
    const cy = (p.y - this.y) / this.scale;
    this.ease();
    this.set(scale, p.x - cx * scale, p.y - cy * scale);
    this.sharpen();
  }

  // With a mouse: the point of the photograph under the pointer comes into view.
  follow(p) {
    const fx = Math.min(1, Math.max(0, (p.x - this.rest.x) / this.pw));
    const fy = Math.min(1, Math.max(0, (p.y - this.rest.y) / this.ph));
    const s = this.scale;
    this.set(s, (this.w - this.pw * s) * fx, (this.h - this.ph * s) * fy);
  }

  reset(animate) {
    clearTimeout(this.tapTimer);
    document.removeEventListener('keydown', this.onDocumentKey);
    this.mouse = false;
    this.setLevel(0);
    if (!this.layer) return;
    const layer = this.layer;
    if (animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.ease();
      this.set(1, this.rest.x, this.rest.y);
      setTimeout(() => layer.remove(), 260);
    } else {
      layer.remove();
    }
    this.layer = null;
    this.scale = 1;
    this.frame.classList.remove('is-zoomed');
    this.frame.setAttribute('aria-pressed', 'false');
  }

  sharpen() {
    const src = this.frame.dataset.zoomSrc;
    if (!src || this.sharpAsked === src) return;
    this.sharpAsked = src;
    const image = new Image();
    image.decoding = 'async';
    image.src = src;
    image
      .decode()
      .then(() => {
        this.sharpReady = src;
        if (this.layer && this.frame.dataset.zoomSrc === src) this.layer.src = src;
      })
      .catch(() => (this.sharpAsked = null));
  }

  local(x, y) {
    const rect = this.frame.getBoundingClientRect();
    return { x: x - rect.left, y: y - rect.top };
  }

  onClick(event) {
    if (this.pointer === 'touch') return;
    if (event.detail === 0) return; // Enter and Space are handled in onKey
    const p = this.local(event.clientX, event.clientY);
    if (this.zoomed && this.level !== 1) {
      this.reset(true);
      return;
    }
    if (this.zoomed) {
      // The second click: closer, at the same point under the pointer.
      this.mouse = true;
      this.setLevel(2);
      this.ease();
      this.scale = this.deeper;
      this.follow(p);
      return;
    }
    if (!this.start()) return;
    this.mouse = true;
    this.setLevel(1);
    this.ease();
    this.scale = this.deep;
    this.follow(p);
    this.sharpen();
    document.addEventListener('keydown', this.onDocumentKey);
  }

  onPointerMove(event) {
    if (!this.mouse || event.pointerType === 'touch') return;
    const p = this.local(event.clientX, event.clientY);
    cancelAnimationFrame(this.frameRequest);
    this.frameRequest = requestAnimationFrame(() => this.follow(p));
  }

  onKey(event) {
    const steps = { ArrowLeft: [1, 0], ArrowRight: [-1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1] };
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const centre = { x: this.frame.clientWidth / 2, y: this.frame.clientHeight / 2 };
      if (!this.zoomed) {
        this.zoomAt(centre, this.deepAt());
        this.setLevel(1);
      } else if (this.level === 1) {
        this.zoomAt(centre, this.deeper);
        this.setLevel(2);
      } else {
        this.reset(true);
      }
    } else if (event.key === 'Escape' && this.zoomed) {
      event.preventDefault();
      event.stopPropagation();
      this.reset(true);
    } else if (steps[event.key] && this.zoomed) {
      event.preventDefault();
      const [dx, dy] = steps[event.key];
      this.ease();
      this.set(this.scale, this.x + dx * this.w * 0.2, this.y + dy * this.h * 0.2);
    }
  }

  onTouchStart(event) {
    const touches = event.touches;
    if (touches.length === 2) {
      // Two fingers: the page itself must not zoom.
      event.preventDefault();
      clearTimeout(this.tapTimer);
      this.touch = null;
      if (!this.start()) return;
      const a = this.local(touches[0].clientX, touches[0].clientY);
      const b = this.local(touches[1].clientX, touches[1].clientY);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      this.pinch = {
        distance: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        cx: (mid.x - this.x) / this.scale,
        cy: (mid.y - this.y) / this.scale,
        scale: this.scale,
      };
    } else if (touches.length === 1 && !this.pinch) {
      const p = this.local(touches[0].clientX, touches[0].clientY);
      this.touch = { start: p, last: p, time: event.timeStamp, moved: false, axis: null, x: this.x, y: this.y };
    }
  }

  onTouchMove(event) {
    const touches = event.touches;
    if (this.pinch && touches.length === 2) {
      event.preventDefault();
      const a = this.local(touches[0].clientX, touches[0].clientY);
      const b = this.local(touches[1].clientX, touches[1].clientY);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const scale = Math.min(this.max, Math.max(1, (this.pinch.scale * Math.hypot(a.x - b.x, a.y - b.y)) / this.pinch.distance));
      this.set(scale, mid.x - this.pinch.cx * scale, mid.y - this.pinch.cy * scale);
      return;
    }
    if (!this.touch || touches.length !== 1) return;
    const p = this.local(touches[0].clientX, touches[0].clientY);
    const dx = p.x - this.touch.start.x;
    const dy = p.y - this.touch.start.y;
    this.touch.last = p;
    if (!this.touch.moved && Math.hypot(dx, dy) > 8) {
      this.touch.moved = true;
      this.touch.axis = Math.abs(dy) > Math.abs(dx) ? 'y' : 'x';
    }
    if (this.scale > 1) {
      // Zoomed: one finger looks around the photograph instead of scrolling.
      event.preventDefault();
      this.set(this.scale, this.touch.x + dx, this.touch.y + dy);
    } else if (this.onDrag && this.touch.axis === 'y') {
      event.preventDefault();
      this.onDrag(dy, false);
    }
  }

  onTouchEnd(event) {
    if (this.pinch) {
      if (event.touches.length < 2) {
        this.pinch = null;
        if (this.scale < 1.05) this.reset(true);
        else this.sharpen();
        // A finger still down carries on looking around from here.
        if (event.touches.length === 1 && this.scale > 1) {
          const p = this.local(event.touches[0].clientX, event.touches[0].clientY);
          this.touch = { start: p, last: p, time: event.timeStamp, moved: true, axis: null, x: this.x, y: this.y };
        }
      }
      return;
    }
    if (!this.touch || event.touches.length) return;
    const touch = this.touch;
    this.touch = null;

    if (this.onDrag && touch.axis === 'y' && this.scale === 1) {
      this.onDrag(touch.last.y - touch.start.y, true);
      return;
    }
    if (touch.moved || event.timeStamp - touch.time > 400) return;

    // A tap: no emulated click, and no zoom of the page itself.
    if (event.cancelable) event.preventDefault();
    const p = touch.start;
    const last = this.lastTap;
    if (last && event.timeStamp - last.time < 320 && Math.hypot(p.x - last.x, p.y - last.y) < 40) {
      this.lastTap = null;
      clearTimeout(this.tapTimer);
      if (this.zoomed) this.reset(true);
      else this.zoomAt(p, this.deepAt());
    } else {
      this.lastTap = { x: p.x, y: p.y, time: event.timeStamp };
      clearTimeout(this.tapTimer);
      if (this.onTap) this.tapTimer = setTimeout(() => this.onTap(this), 320);
    }
  }

  // The double-tap depth, measured before the copy is laid over the photograph.
  deepAt() {
    if (!this.start()) return 2.5;
    return this.deep;
  }
}

/*
 * <nagada-product>: the product page. Choosing a cloth or a size finds the
 * matching variant, then keeps everything in step with it: the hidden variant
 * id, the address (?variant=), the price, the photograph, the chosen-value
 * labels, the WhatsApp message, the cloth close-up at the end of the
 * photographs and the "Selected cloth" section below.
 * On phones it counts the photographs as you swipe, and shows the order button
 * at the bottom of the screen once the main one has scrolled away.
 * With zoom on, each photograph zooms in place (NagadaZoom), and a tap on a
 * touch screen opens them full screen.
 */

class NagadaProduct extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('script[data-variants]');
    if (!data) return;
    this.variants = JSON.parse(data.textContent);
    this.fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-index]'));
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
    this.stage = this.querySelector('[data-stage]');
    this.count = this.querySelector('[data-count]');

    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);

    // The enlarged cloth: on hover with a mouse, or when a swatch gets keyboard focus.
    this.addEventListener('pointerover', (event) => {
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      const swatch = event.target.closest('.nd-opt__swatch');
      if (swatch) this.preview(swatch);
    });
    this.addEventListener('pointerout', (event) => {
      const row = event.target.closest('.nd-opt__swatches');
      if (row && !row.contains(event.relatedTarget)) this.hidePreview(row);
    });
    this.addEventListener('focusin', (event) => {
      const input = event.target;
      if (input.matches && input.matches('.nd-opt__swatch input') && input.matches(':focus-visible')) this.preview(input.closest('.nd-opt__swatch'));
    });
    this.addEventListener('focusout', (event) => {
      const row = event.target.closest && event.target.closest('.nd-opt__swatches');
      if (row && !row.contains(event.relatedTarget)) this.hidePreview(row);
    });

    if (this.stage && this.count) {
      this.onScroll = () => {
        const shown = this.shownSlides();
        const i = Math.round(this.stage.scrollLeft / Math.max(1, this.stage.clientWidth));
        this.count.textContent = `${Math.min(i + 1, shown.length)} / ${shown.length}`;
      };
      this.stage.addEventListener('scroll', this.onScroll, { passive: true });
    }

    if (this.querySelector('.nd-prod--zoom')) {
      this.zooms = this.slides.map(
        (slide) =>
          new NagadaZoom(slide, {
            onTap: () => this.openViewer(slide),
            onZoom: (zoom) => this.zooms.forEach((other) => other !== zoom && other.reset(false)),
          })
      );
      this.viewer = this.querySelector('[data-zoomview]');
      this.width = window.innerWidth;
      this.onResize = () => {
        if (window.innerWidth === this.width) return; // a phone's address bar coming and going
        this.width = window.innerWidth;
        this.resetZoom();
      };
      window.addEventListener('resize', this.onResize);
    }

    const sticky = this.querySelector('[data-sticky-buy]');
    const main = this.querySelector('.nd-prod__buy');
    if (sticky && main && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', past);
      });
      this.observer.observe(main);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    if (this.stage && this.onScroll) this.stage.removeEventListener('scroll', this.onScroll);
    if (this.observer) this.observer.disconnect();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    (this.zooms || []).forEach((zoom) => zoom.destroy());
    (this.viewZooms || []).forEach((zoom) => zoom.destroy());
  }

  // The photographs on show: the cloth close-up drops out for a cloth without a photograph.
  shownSlides() {
    return this.slides.filter((slide) => !slide.hasAttribute('data-off'));
  }

  resetZoom() {
    (this.zooms || []).forEach((zoom) => zoom.reset(false));
  }

  // The cloth close-up follows the chosen cloth.
  updateCloth(input) {
    const slide = this.querySelector('[data-slide="cloth"]');
    if (!slide || !input) return;
    const src = input.dataset.cloth || '';
    const thumb = this.querySelector('[data-thumb="cloth"]');
    slide.toggleAttribute('data-off', !src);
    if (thumb) thumb.hidden = !src;
    if (src && slide.dataset.zoomSrc !== src) {
      const zoom = (this.zooms || []).find((z) => z.frame === slide);
      if (zoom) zoom.reset(false);
      slide.dataset.zoomSrc = src;
      // The small preview (usually already loaded) at once, so the frame never shows the last cloth;
      // the full one as soon as it arrives.
      const image = slide.querySelector('[data-cloth-image]');
      image.src = input.dataset.preview || src;
      image.alt = input.value;
      const full = new Image();
      full.src = src;
      full
        .decode()
        .then(() => slide.dataset.zoomSrc === src && (image.src = src))
        .catch(() => {});
      slide.querySelector('[data-cloth-name]').textContent = input.value;
      if (slide.hasAttribute('aria-label')) slide.setAttribute('aria-label', slide.getAttribute('aria-label').replace(/,[^,]*$/, `, ${input.value}`));
      const thumbImage = thumb && thumb.querySelector('[data-cloth-thumb]');
      if (thumbImage && input.dataset.preview) thumbImage.src = input.dataset.preview;
    }
    if (!src && !slide.hidden && !window.matchMedia('(max-width: 749px)').matches) this.showMedia(this.slides[0].dataset.slide);
    if (this.onScroll) this.onScroll();
  }

  // Full screen, on a touch screen: the same photographs, one at a time, to swipe and zoom.
  openViewer(slide) {
    const viewer = this.viewer;
    if (!viewer || viewer.open) return;
    const shown = this.shownSlides();
    const start = Math.max(0, shown.indexOf(slide));
    const strip = viewer.querySelector('[data-zoomview-strip]');
    const count = viewer.querySelector('[data-zoomview-count]');
    this.resetZoom();

    const frames = shown.map((source) => {
      const frame = document.createElement('div');
      frame.className = 'nd-zoomview__slide';
      if (source.dataset.zoomSrc) frame.dataset.zoomSrc = source.dataset.zoomSrc;
      const image = source.querySelector('img').cloneNode();
      image.removeAttribute('fetchpriority');
      image.loading = 'eager';
      if (image.sizes) image.sizes = '100vw';
      frame.append(image);
      const name = source.querySelector('.nd-prod__cloth-name');
      if (name) frame.append(name.cloneNode(true));
      return frame;
    });
    strip.replaceChildren(...frames);
    (this.viewZooms || []).forEach((zoom) => zoom.destroy());
    this.viewZooms = frames.map((frame) => new NagadaZoom(frame, { onDrag: (dy, done) => this.dragViewer(dy, done) }));

    const current = () => Math.round(strip.scrollLeft / Math.max(1, strip.clientWidth));
    let seen = start; // kept while open: a closed dialog has no width to measure
    const onStripScroll = () => {
      const i = current();
      seen = i;
      if (count) count.textContent = frames.length > 1 ? `${Math.min(i + 1, frames.length)} / ${frames.length}` : '';
      this.viewZooms.forEach((zoom, k) => k !== i && zoom.reset(false));
    };
    const onKey = (event) => {
      const step = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
      if (!step) return;
      event.preventDefault();
      strip.scrollTo({ left: (current() + step) * strip.clientWidth });
    };
    const onClose = () => {
      const i = seen;
      strip.removeEventListener('scroll', onStripScroll);
      viewer.removeEventListener('keydown', onKey);
      viewer.querySelector('[data-zoomview-close]').removeEventListener('click', close);
      document.documentElement.classList.remove('nd-zoom-open');
      this.viewZooms.forEach((zoom) => zoom.destroy());
      this.viewZooms = [];
      strip.replaceChildren();
      // Back on the page at the photograph last seen.
      const last = shown[i];
      if (last) {
        if (window.matchMedia('(max-width: 749px)').matches && this.stage) this.stage.scrollTo({ left: last.offsetLeft - this.stage.offsetLeft });
        else this.showMedia(last.dataset.slide);
      }
    };
    const close = () => viewer.close();

    strip.addEventListener('scroll', onStripScroll, { passive: true });
    viewer.addEventListener('keydown', onKey);
    viewer.addEventListener('close', onClose, { once: true });
    viewer.querySelector('[data-zoomview-close]').addEventListener('click', close);
    document.documentElement.classList.add('nd-zoom-open');
    viewer.style.removeProperty('--nd-drag');
    viewer.showModal();
    viewer.focus({ preventScroll: true }); // not the Close button: no focus ring on a tap
    strip.scrollLeft = start * strip.clientWidth;
    onStripScroll();
  }

  // Drag the full-screen view up or down to close it.
  dragViewer(dy, done) {
    const viewer = this.viewer;
    if (!viewer) return;
    if (!done) {
      viewer.classList.remove('is-settling');
      viewer.style.setProperty('--nd-drag', `${dy}px`);
      return;
    }
    if (Math.abs(dy) > 90) {
      viewer.close();
      return;
    }
    viewer.classList.add('is-settling');
    viewer.style.setProperty('--nd-drag', '0px');
  }

  preview(swatch) {
    const row = swatch.closest('.nd-opt__swatches');
    const card = row && row.querySelector('[data-preview-card]');
    const input = swatch.querySelector('input');
    if (!card || !input) return;
    const src = input.dataset.preview || input.dataset.image;
    if (!src) {
      card.classList.remove('is-visible');
      return;
    }
    const image = card.querySelector('[data-preview-image]');
    if (image.getAttribute('src') !== src) image.src = src;
    card.querySelector('[data-preview-name]').textContent = input.value;
    const x = swatch.offsetLeft + swatch.offsetWidth / 2 - card.offsetWidth / 2;
    card.style.setProperty('--nd-preview-x', `${Math.round(Math.max(0, Math.min(x, row.clientWidth - card.offsetWidth)))}px`);
    card.style.setProperty('--nd-preview-y', `${Math.round(swatch.offsetTop - card.offsetHeight - 8)}px`);
    card.classList.add('is-visible');
  }

  hidePreview(row) {
    const card = row.querySelector('[data-preview-card]');
    if (card) card.classList.remove('is-visible');
  }

  onClick(event) {
    const thumb = event.target.closest('[data-thumb]');
    if (thumb) this.showMedia(thumb.dataset.thumb);
  }

  showMedia(id) {
    if (!id) return;
    const target = this.slides.find((slide) => slide.dataset.slide === String(id));
    if (!target) return;
    this.resetZoom();
    if (window.matchMedia('(max-width: 749px)').matches && this.stage) {
      this.stage.scrollTo({ left: target.offsetLeft - this.stage.offsetLeft, behavior: 'smooth' });
      return;
    }
    this.slides.forEach((slide) => {
      slide.hidden = slide !== target;
    });
    this.thumbs.forEach((thumb) => {
      thumb.setAttribute('aria-current', thumb.dataset.thumb === String(id) ? 'true' : 'false');
    });
  }

  selectedOptions() {
    const options = [];
    this.fieldsets.forEach((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      options[Number(fieldset.dataset.optionIndex)] = checked ? checked.value : null;
    });
    return options;
  }

  onChange(event) {
    const fieldset = event.target.closest('fieldset[data-option-index]');
    if (!fieldset) return;

    const label = fieldset.querySelector('[data-option-value]');
    if (label) label.textContent = event.target.value;

    const chosen = this.selectedOptions();
    const variant = this.variants.find((v) => v.options.every((value, i) => chosen[i] == null || chosen[i] === value));
    if (!variant) return;

    const idInput = this.querySelector('[data-variant-id]');
    if (idInput) idInput.value = variant.id;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState(null, '', url.toString());

    const price = this.querySelector('[data-price]');
    if (price) price.textContent = variant.free ? this.dataset.zero : variant.price;

    const add = this.querySelector('[data-add]');
    if (add) {
      add.disabled = !variant.available;
      add.textContent = variant.available ? add.dataset.addLabel : add.dataset.soldLabel;
    }

    const stock = this.querySelector('[data-stock]');
    if (stock) {
      stock.textContent = variant.available ? stock.dataset.now : stock.dataset.made;
      stock.classList.toggle('nd-prod__stock--now', variant.available);
    }

    if (variant.media) this.showMedia(variant.media);

    const cloth = this.findValue(/fabric|cloth|colou?r/i, chosen);
    const size = this.findValue(/size/i, chosen);
    this.updateWhatsApp(variant, cloth, size);

    const clothInput = cloth ? this.querySelector(`fieldset[data-option-index="${cloth.index}"] input:checked`) : null;
    this.updateCloth(clothInput);
    document.dispatchEvent(
      new CustomEvent('nagada:variant-change', {
        detail: { variant, cloth: cloth ? cloth.value : '', clothImage: clothInput ? clothInput.dataset.image : '' },
      })
    );
  }

  findValue(pattern, chosen) {
    for (const fieldset of this.fieldsets) {
      const legend = fieldset.querySelector('legend');
      const name = fieldset.dataset.optionName || (legend ? legend.firstChild.textContent : '');
      if (pattern.test(name)) {
        const index = Number(fieldset.dataset.optionIndex);
        return { index, value: chosen[index] };
      }
    }
    return null;
  }

  updateWhatsApp(variant, cloth, size) {
    const links = this.querySelectorAll('[data-wa-link]');
    if (!links.length || !this.dataset.wa) return;
    const link = `${this.dataset.url}?variant=${variant.id}`;
    const message = (this.dataset.waMessage || '')
      .replace('[product]', this.dataset.title || '')
      .replace('[cloth]', cloth && cloth.value ? cloth.value : '')
      .replace('[size]', size && size.value ? size.value : '')
      .replace('[link]', link)
      .replace(' in ,', ',')
      .replace(' in .', '.')
      .replace(', size .', '.')
      .replace(', size ,', ',');
    const href = `https://wa.me/${this.dataset.wa}?text=${encodeURIComponent(message)}`;
    links.forEach((a) => (a.href = href));
  }
}

if (!customElements.get('nagada-product')) customElements.define('nagada-product', NagadaProduct);

/* "Selected cloth" follows the cloth chosen on the product page. */
document.addEventListener('nagada:variant-change', (event) => {
  const { cloth, clothImage } = event.detail || {};
  document.querySelectorAll('[data-cloth-detail]').forEach((section) => {
    const name = section.querySelector('[data-cloth-name]');
    if (name && cloth) name.textContent = cloth;
    const image = section.querySelector('[data-cloth-image]');
    if (image && clothImage) {
      image.src = clothImage;
      image.alt = cloth || '';
      image.hidden = false;
    }
  });
});

/*
 * <nagada-header>: the main menu.
 * - Below 1200px the menu lives in the Menu drawer (CSS). Above it, the header
 *   still folds into the drawer if the links would wrap onto a second line,
 *   for example when another link is added in the theme editor.
 * - Desktop panels open on hover and keyboard focus (CSS). On touch screens the
 *   first tap on an item with a panel opens it, and the second follows the link.
 * - Escape closes an open panel or the drawer. While the drawer is open the
 *   page behind it does not scroll.
 */

class NagadaHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('.nd-header');
    this.nav = this.querySelector('.nd-header__nav');
    this.list = this.querySelector('.nd-nav');
    this.utils = this.querySelector('.nd-header__utils');
    this.logo = this.querySelector('.nd-header__logo');
    this.drawer = this.querySelector('.nd-header__menu');
    this.sheet = this.querySelector('.nd-nav__sheet');
    this.indicator = this.querySelector('.nd-nav__indicator');
    if (!this.header) return;

    this.items = Array.from(this.querySelectorAll('.nd-nav__item'));
    this.active = null;
    if (this.list && this.sheet && this.indicator) this.list.classList.add('is-enhanced');

    this.measure = this.measure.bind(this);
    this.onResize = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(this.measure);
    };

    // Hover with intent: open after a short pause, switch only when the pointer settles on an item,
    // close only after it has left the menu for a moment. Diagonal moves to a panel do not flip items.
    this.items.forEach((item) => {
      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch' || !this.inline()) return;
        clearTimeout(this.closeTimer);
        clearTimeout(this.switchTimer);
        const delay = this.active ? (item === this.active ? 0 : 110) : 70;
        this.switchTimer = setTimeout(() => this.show(item), delay);
      });
      item.addEventListener('pointerleave', () => clearTimeout(this.switchTimer));
    });
    if (this.list) {
      this.list.addEventListener('pointerenter', () => clearTimeout(this.closeTimer));
      this.list.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        clearTimeout(this.switchTimer);
        this.closeTimer = setTimeout(() => this.hide(), 260);
      });
      this.list.addEventListener('focusin', (event) => {
        const item = event.target.closest('.nd-nav__item');
        // Only keyboard focus opens a panel here: a mouse has hover, a tap has the first-tap rule below.
        // Escape sends focus back to the item's link; that must not reopen what it just closed.
        if (item && this.inline() && !this.pressing && !item.classList.contains('is-dismissed')) this.show(item, true);
      });
      this.list.addEventListener('focusout', (event) => {
        if (!this.list.contains(event.relatedTarget)) this.hide();
      });
    }

    this.onPointerDown = (event) => {
      this.pointer = event.pointerType;
      this.pressing = true;
      clearTimeout(this.pressTimer);
      this.pressTimer = setTimeout(() => (this.pressing = false), 600);
    };
    this.onClick = (event) => {
      const top = event.target.closest('[data-nav-top]');
      if (!top || this.pointer !== 'touch') return;
      const item = top.closest('.nd-nav__item');
      if (item === this.active) return;
      event.preventDefault();
      this.show(item, true);
    };
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.hide();
    };
    this.onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (this.drawer && this.drawer.open) {
        this.drawer.open = false;
        this.drawer.querySelector('summary').focus();
        return;
      }
      const item = event.target.closest('.nd-nav__item');
      if (item) item.classList.add('is-dismissed');
      this.hide();
      if (item) item.querySelector('.nd-nav__link').focus();
    };
    this.onDismissEnd = (event) => {
      const item = event.target.closest('.nd-nav__item');
      if (item && !item.contains(event.relatedTarget)) item.classList.remove('is-dismissed');
    };
    this.onToggle = () => {
      const open = this.drawer.open || Boolean(document.querySelector('dialog.nd-search[open]'));
      this.header.style.setProperty('--nd-header-bottom', `${Math.max(0, this.header.getBoundingClientRect().bottom)}px`);
      document.documentElement.classList.toggle('nd-menu-open', open);
    };

    window.addEventListener('resize', this.onResize);
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('mouseout', this.onDismissEnd);
    this.addEventListener('focusout', this.onDismissEnd);
    document.addEventListener('click', this.onDocumentClick);
    if (this.drawer) this.drawer.addEventListener('toggle', this.onToggle);

    this.measure();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.measure);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('click', this.onDocumentClick);
    document.documentElement.classList.remove('nd-menu-open');
  }

  inline() {
    return window.innerWidth >= 1200 && !this.header.classList.contains('nd-header--compact');
  }

  // Open one item's panel on the shared sheet; switching between items keeps the sheet and moves the line.
  show(item, immediate = false) {
    clearTimeout(this.closeTimer);
    const panel = item.querySelector('.nd-nav__panel');
    if (this.active && this.active !== item) this.active.classList.remove('is-open');
    this.active = item;
    item.classList.add('is-open');
    this.moveIndicator(item.querySelector('.nd-nav__link'), immediate && !this.header.classList.contains('is-menu-open'));
    if (panel && this.sheet) {
      this.header.style.setProperty('--nd-sheet-h', `${panel.offsetHeight}px`);
      this.header.classList.add('is-menu-open');
    } else {
      this.header.classList.remove('is-menu-open');
    }
  }

  hide() {
    clearTimeout(this.switchTimer);
    clearTimeout(this.closeTimer);
    if (this.active) this.active.classList.remove('is-open');
    this.active = null;
    this.header.classList.remove('is-menu-open');
    this.moveIndicator(this.current());
  }

  current() {
    const link = this.querySelector('.nd-nav__link[aria-current]');
    return link || null;
  }

  // The sliding line sits where an underline would: under the words of the link, not its whole box.
  moveIndicator(link, instant = false) {
    const line = this.indicator;
    if (!line) return;
    if (!link || !this.inline()) {
      line.classList.remove('is-visible');
      return;
    }
    const box = this.header.getBoundingClientRect();
    const range = document.createRange();
    range.selectNodeContents(link);
    const text = range.getBoundingClientRect();
    const spacing = parseFloat(getComputedStyle(link).letterSpacing) || 0;
    line.classList.toggle('is-instant', instant || !line.classList.contains('is-visible'));
    line.style.setProperty('--nd-ind-x', `${Math.round(text.left - box.left)}px`);
    line.style.setProperty('--nd-ind-y', `${Math.round(text.bottom - box.top + 4)}px`);
    line.style.setProperty('--nd-ind-w', `${Math.max(0, Math.round(text.width - spacing))}px`);
    line.classList.add('is-visible');
    if (line.classList.contains('is-instant')) requestAnimationFrame(() => line.classList.remove('is-instant'));
  }

  // Line each panel's links up under its own item, moved left only as far as needed to clear the photograph.
  placePanels() {
    const box = this.header.getBoundingClientRect();
    const gutter = parseFloat(getComputedStyle(this.header).paddingLeft) || 16;
    this.items.forEach((item) => {
      const panel = item.querySelector('.nd-nav__panel');
      const link = item.querySelector('.nd-nav__link');
      if (!panel || !link) return;
      const list = panel.querySelector('.nd-nav__children');
      const feature = panel.querySelector('.nd-nav__feature');
      const left = link.getBoundingClientRect().left - box.left;
      const room = box.width - gutter - (feature ? feature.offsetWidth + 40 : 0);
      const width = list ? list.offsetWidth : 0;
      const x = Math.max(gutter, Math.min(left, room - width));
      panel.style.setProperty('--nd-item-x', `${Math.round(x)}px`);
    });
  }

  measure() {
    const header = this.header;
    header.classList.remove('nd-header--compact');

    if (window.innerWidth >= 1200 && this.nav && this.logo) {
      const first = this.items[0];
      const last = this.items[this.items.length - 1];
      const middle = this.logo.offsetTop + this.logo.offsetHeight / 2;
      const wraps =
        (first && last && last.offsetTop > first.offsetTop + 4) ||
        this.nav.offsetTop > middle ||
        (this.utils && this.utils.offsetTop > middle);
      header.classList.toggle('nd-header--compact', Boolean(wraps));
    }

    if (this.inline()) {
      if (this.drawer && this.drawer.open) this.drawer.open = false;
      this.placePanels();
      if (this.active) this.show(this.active, true);
      else this.moveIndicator(this.current(), true);
    } else {
      this.hide();
    }
  }
}

if (!customElements.get('nagada-header')) customElements.define('nagada-header', NagadaHeader);

/*
 * NAGADA search.
 * Shopify's own search matches by meaning ("faiza" returns most of the shop), so
 * garments and cloths are found here instead, in an exact index of every product
 * and every Fabric entry (sections/nagada-search-index and nagada-cloth-index,
 * fetched once and kept for ten minutes). Words can mean the same thing
 * (burgundy = burgandy), set in Theme settings > NAGADA search and filters.
 */

const NagadaSearch = (() => {
  let pending = null;

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9%/ ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const escape = (value) =>
    String(value || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  function synonymGroups(text) {
    return String(text || '')
      .split(/\n/)
      .map((line) => line.split('='))
      .filter((parts) => parts.length === 2)
      .map(([left, right]) => [left, ...right.split(',')].map(normalize).filter(Boolean));
  }

  function variants(token, groups) {
    const out = new Set([token]);
    if (token.length > 3 && token.endsWith('s')) out.add(token.slice(0, -1));
    groups.forEach((group) => {
      if (group.some((word) => word === token || (token.length >= 4 && word.startsWith(token)) || word === token.replace(/s$/, ''))) {
        group.forEach((word) => out.add(word));
      }
    });
    return [...out];
  }

  // A word matches when it starts a word of the text; longer words may also sit inside one.
  const has = (text, word) => (' ' + text).includes(' ' + word) || (word.length >= 4 && text.includes(word));

  function load(url) {
    if (pending) return pending;
    const key = `nd-search:${url}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.t < 10 * 60 * 1000) return (pending = Promise.resolve(prepare(cached.d)));
    } catch (error) {}
    pending = fetch(url, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((sections) => {
        const read = (html) => {
          const doc = new DOMParser().parseFromString(html || '', 'text/html');
          const script = doc.querySelector('script[data-nd-index]');
          return script ? JSON.parse(script.textContent) : [];
        };
        const data = { products: read(sections['nagada-search-index']), cloths: read(sections['nagada-cloth-index']) };
        try {
          sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: data }));
        } catch (error) {}
        return prepare(data);
      })
      .catch(() => {
        pending = null;
        return { products: [], cloths: [] };
      });
    return pending;
  }

  function prepare(data) {
    data.products.forEach((p) => {
      p._ = {
        title: normalize(p.t),
        garment: normalize(p.g),
        cloths: normalize((p.n || []).join(' ')),
        families: normalize((p.f || []).join(' ')),
        line: normalize(p.l),
      };
    });
    data.cloths.forEach((c) => {
      c._ = { name: normalize(c.n), families: normalize((c.f || []).join(' ')) };
    });
    return data;
  }

  function find(data, config, query) {
    const groups = synonymGroups(config.synonyms);
    const tokens = normalize(query).split(' ').filter(Boolean);
    if (!tokens.length) return null;
    const options = tokens.map((token) => variants(token, groups));

    const garments = [];
    data.products.forEach((p) => {
      let total = 0;
      for (const words of options) {
        let best = 0;
        for (const word of words) {
          if (has(p._.title, word)) best = Math.max(best, 6);
          else if (has(p._.garment, word)) best = Math.max(best, 5);
          else if (has(p._.cloths, word)) best = Math.max(best, 3);
          else if (has(p._.families, word)) best = Math.max(best, 2);
          else if (has(p._.line, word)) best = Math.max(best, 1);
        }
        if (!best) return;
        total += best;
      }
      garments.push({ item: p, score: total });
    });
    garments.sort((a, b) => b.score - a.score || a.item.t.localeCompare(b.item.t));

    const cloths = data.cloths.filter((c) =>
      options.every((words) => words.some((word) => has(c._.name, word) || has(c._.families, word)))
    );

    const pages = (config.shortcuts || []).filter((page) => {
      const text = normalize([page.label, page.note, page.words].join(' '));
      return options.every((words) => words.some((word) => has(text, word)));
    });

    return { garments: garments.map((g) => g.item), cloths, pages };
  }

  function card(p) {
    const swatches = (p.s || []).filter(Boolean).slice(0, 5).map((src) => `<span class="nd-swatch" style="background-image: url('${escape(src)}');"></span>`).join('');
    return `<a class="nd-pcard" href="${escape(p.u)}">
      <span class="nd-pcard__media" style="aspect-ratio: 3 / 4;">
        ${p.i ? `<img class="nd-pcard__img" src="${escape(p.i)}" alt="${escape(p.t)}" loading="lazy" width="600" height="800">` : ''}
        ${p.c ? `<span class="nd-pcard__cloth"><img class="nd-pcard__img" src="${escape(p.c)}" alt="" loading="lazy" width="600" height="800"></span>` : ''}
      </span>
      <span class="nd-pcard__info">
        <span class="nd-pcard__row">
          <span class="nd-pcard__name nd-caps"><span class="nd-pcard__title">${escape(p.t)}</span>${p.l ? `<span class="nd-pcard__fabric">${escape(p.l)}</span>` : ''}</span>
          ${swatches ? `<span class="nd-pcard__swatches">${swatches}</span>` : ''}
        </span>
        ${p.p ? `<span class="nd-pcard__price">${escape(p.p)}</span>` : ''}
      </span>
    </a>`;
  }

  const cloth = (c, searchUrl) =>
    `<li><a href="${escape(searchUrl)}?q=${encodeURIComponent(c.n)}"><span class="nd-search__swatch"${c.i ? ` style="background-image: url('${escape(c.i)}');"` : ''}></span><span>${escape(c.n)}</span></a></li>`;

  const page = (p) =>
    `<li><a href="${escape(p.url)}"><span>${escape(p.label)}</span>${p.note ? `<span class="nd-search__note">${escape(p.note)}</span>` : ''}</a></li>`;

  function config() {
    const script = document.querySelector('script[data-search-config]');
    try {
      return script ? JSON.parse(script.textContent) : {};
    } catch (error) {
      return {};
    }
  }

  return { load, find, card, cloth, page, config, escape, normalize };
})();

/* <nagada-search-panel>: the search that opens over the page from the header. */
class NagadaSearchPanel extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.input = this.querySelector('[data-search-input]');
    this.start = this.querySelector('[data-search-start]');
    this.results = this.querySelector('[data-search-results]');
    this.config = NagadaSearch.config();
    if (!this.dialog || !this.input) return;

    this.onDocumentClick = (event) => {
      const opener = event.target.closest('[data-search-open]');
      if (!opener || event.metaKey || event.ctrlKey) return;
      event.preventDefault();
      this.open();
    };
    this.onPrefetch = (event) => {
      if (event.target.closest && event.target.closest('[data-search-open]')) NagadaSearch.load(this.dataset.indexUrl);
    };
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('pointerover', this.onPrefetch, { passive: true });
    document.addEventListener('focusin', this.onPrefetch);

    this.querySelector('[data-search-close]').addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => document.documentElement.classList.remove('nd-menu-open'));
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.dialog.close();
      }
    });
    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update(), 120);
    });
    this.querySelectorAll('[data-search-term]').forEach((link) =>
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.input.value = link.dataset.searchTerm;
        this.update();
        this.input.focus();
      })
    );
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('pointerover', this.onPrefetch);
    document.removeEventListener('focusin', this.onPrefetch);
  }

  open() {
    const drawer = document.querySelector('.nd-header__menu[open]');
    if (drawer) drawer.open = false;
    if (!this.dialog.open) this.dialog.showModal();
    // Closing the drawer releases its scroll lock a moment later; take it back after that.
    document.documentElement.classList.add('nd-menu-open');
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (this.dialog.open) document.documentElement.classList.add('nd-menu-open');
    }));
    this.input.focus();
    this.input.select();
    NagadaSearch.load(this.dataset.indexUrl);
  }

  async update() {
    const query = this.input.value.trim();
    if (!query) {
      this.start.hidden = false;
      this.results.hidden = true;
      return;
    }
    const data = await NagadaSearch.load(this.dataset.indexUrl);
    if (this.input.value.trim() !== query) return;
    const found = NagadaSearch.find(data, this.config, query) || { garments: [], cloths: [], pages: [] };
    const searchUrl = this.dataset.searchUrl;

    const fill = (name, items, render, limit) => {
      const group = this.results.querySelector(`[data-group="${name}"]`);
      group.hidden = items.length === 0;
      group.querySelector('[data-list]').innerHTML = items.slice(0, limit).map(render).join('');
      const count = group.querySelector('[data-count]');
      if (count) count.textContent = items.length > limit ? `${limit} of ${items.length}` : items.length ? String(items.length) : '';
    };
    fill('garments', found.garments, NagadaSearch.card, 8);
    fill('cloths', found.cloths, (c) => NagadaSearch.cloth(c, searchUrl), 10);
    fill('pages', found.pages, NagadaSearch.page, 6);

    const none = this.results.querySelector('[data-search-none]');
    const empty = !found.garments.length && !found.cloths.length && !found.pages.length;
    none.hidden = !empty;
    none.textContent = empty ? (this.config.text?.none || '').replace('[terms]', query) : '';
    this.results.querySelector('[data-search-all]').href = `${searchUrl}?q=${encodeURIComponent(query)}`;
    this.start.hidden = true;
    this.results.hidden = false;
  }
}

if (!customElements.get('nagada-search-panel')) customElements.define('nagada-search-panel', NagadaSearchPanel);

/* <nagada-search-results>: the search page. Exact matches first; Shopify's results follow as close matches. */
class NagadaSearchResults extends HTMLElement {
  async connectedCallback() {
    const terms = this.dataset.terms;
    const exact = this.querySelector('[data-exact]');
    this.querySelector('[data-try-hidden]')?.setAttribute('hidden', '');
    if (!terms || !exact) return;

    const data = await NagadaSearch.load(this.dataset.indexUrl);
    const found = NagadaSearch.find(data, NagadaSearch.config(), terms);
    if (!found) return;
    const searchUrl = this.dataset.searchUrl;

    const fill = (name, items, render) => {
      const group = this.querySelector(`[data-exact-group="${name}"]`);
      group.hidden = items.length === 0;
      group.querySelector('[data-list]').innerHTML = items.map(render).join('');
      const count = group.querySelector('[data-count]');
      if (count) count.textContent = items.length ? String(items.length) : '';
    };
    fill('garments', found.garments, NagadaSearch.card);
    fill('cloths', found.cloths.slice(0, 48), (c) => NagadaSearch.cloth(c, searchUrl));
    fill('pages', found.pages, NagadaSearch.page);
    exact.hidden = !(found.garments.length || found.cloths.length || found.pages.length);

    // Shopify's results: drop what is already shown, call the rest close matches, and keep a few.
    const shown = new Set([...found.garments.map((g) => g.u), ...found.pages.map((p) => p.url)]);
    this.querySelectorAll('[data-server-item]').forEach((item) => {
      if (shown.has(item.dataset.serverItem)) item.hidden = true;
    });
    const server = this.querySelector('[data-server-products]');
    if (server) {
      const left = [...server.querySelectorAll('[data-server-item]:not([hidden])')];
      left.slice(8).forEach((item) => (item.hidden = true));
      if (!left.length) server.hidden = true;
      const label = server.querySelector('[data-server-label]');
      label.textContent = label.dataset.closeLabel;
    }
    this.querySelector('.nd-pagination')?.setAttribute('hidden', '');

    const nothing = !found.garments.length && !found.cloths.length && !found.pages.length;
    const none = this.querySelector('[data-none]');
    if (none) none.hidden = !nothing;

    const parts = [];
    if (found.garments.length) parts.push(`${found.garments.length} ${found.garments.length === 1 ? 'garment' : 'garments'}`);
    if (found.cloths.length) parts.push(`${found.cloths.length} ${found.cloths.length === 1 ? 'cloth' : 'cloths'}`);
    if (found.pages.length) parts.push(`${found.pages.length} ${found.pages.length === 1 ? 'page' : 'pages'}`);
    const summary = this.querySelector('[data-summary]');
    if (summary) summary.textContent = parts.length ? `${parts.join(', ')} for “${terms}”` : '';
  }
}

if (!customElements.get('nagada-search-results')) customElements.define('nagada-search-results', NagadaSearchResults);

/*
 * Browsing long lists: <nagada-collection> and <nagada-fabrics> show the first
 * few items, then more as the visitor scrolls near the end (or presses Show more);
 * chips filter by garment type and cloth family, and a chip that would show nothing
 * with the other filters is greyed out or hidden; a search box narrows cloths by name.
 * Chosen filters go in the address (?garment=coats&cloth=ikat) so a filtered page can be shared.
 */

class NagadaBrowse extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-item]'));
    if (!this.items.length) return;
    this.step = Number(this.dataset.step || this.dataset.initial || 12);
    this.limit = Number(this.dataset.initial || 12);
    this.filters = {};
    this.find = this.querySelector('[data-find]');

    const params = new URLSearchParams(window.location.search);
    this.querySelectorAll('[data-filter]').forEach((chip) => {
      const group = chip.dataset.filter;
      if (params.get(group) === chip.dataset.value && chip.dataset.value) this.filters[group] = chip.dataset.value;
    });

    const tools = this.querySelector('[data-tools]');
    if (tools) tools.hidden = false;

    this.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-filter]');
      if (chip) {
        this.filters[chip.dataset.filter] = chip.dataset.value;
        this.limit = this.step;
        this.apply(true);
        return;
      }
      if (event.target.closest('[data-more]')) this.more();
    });
    if (this.find) {
      this.find.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          this.limit = this.step;
          this.apply(false);
        }, 120);
      });
    }
    this.setupViews();
    this.apply(false);
    this.setupAutoMore();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  // Shows the next batch on its own when the Show more row comes near the screen.
  setupAutoMore() {
    this.moreRow = this.querySelector('[data-more-row]');
    if (!this.moreRow || !this.hasAttribute('data-auto-more') || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.more();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    this.observer.observe(this.moreRow);
  }

  more() {
    if (!this.moreRow || this.moreRow.hidden) return;
    this.limit += this.step;
    this.apply(false);
    // If the row is still near the screen after the new items appear, keep going.
    if (this.observer) {
      requestAnimationFrame(() => {
        if (this.moreRow.hidden) return;
        const top = this.moreRow.getBoundingClientRect().top;
        if (top < window.innerHeight + 600) this.more();
      });
    }
  }

  // skip: a filter group to leave out, to count what each of its chips would show.
  matches(item, skip) {
    for (const [group, value] of Object.entries(this.filters)) {
      if (!value || group === skip) continue;
      const own = (item.dataset[group] || '').split(' ');
      if (!own.includes(value)) return false;
    }
    if (this.find && this.find.value.trim()) {
      const words = NagadaSearch.normalize(this.find.value).split(' ').filter(Boolean);
      const name = NagadaSearch.normalize(item.dataset.name || item.textContent);
      if (!words.every((word) => (' ' + name).includes(' ' + word) || (word.length >= 3 && name.includes(word)))) return false;
    }
    return true;
  }

  apply(updateUrl) {
    let total = 0;
    let shown = 0;
    this.items.forEach((item) => {
      const ok = this.matches(item);
      if (ok) total += 1;
      const visible = ok && total <= this.limit;
      if (visible) shown += 1;
      item.hidden = !visible;
    });

    this.querySelectorAll('[data-filter]').forEach((chip) => {
      const group = chip.dataset.filter;
      const value = chip.dataset.value;
      const pressed = (this.filters[group] || '') === value;
      chip.setAttribute('aria-pressed', String(pressed));
      if (!value || pressed) {
        chip.disabled = false;
        return;
      }
      // How many items this chip would show alongside the other filters.
      const count = this.items.filter((item) => this.matches(item, group) && (item.dataset[group] || '').split(' ').includes(value)).length;
      chip.disabled = count === 0;
    });

    const row = this.querySelector('[data-more-row]');
    if (row) {
      row.hidden = total <= shown;
      const count = row.querySelector('[data-count]');
      if (count) count.textContent = (this.dataset.countText || '[shown] of [total]').replace('[shown]', shown).replace('[total]', total);
    }
    const empty = this.querySelector('[data-empty]');
    if (empty) empty.hidden = total > 0;

    if (updateUrl) {
      const url = new URL(window.location.href);
      Object.entries(this.filters).forEach(([group, value]) => (value ? url.searchParams.set(group, value) : url.searchParams.delete(group)));
      window.history.replaceState(null, '', url.toString());
    }
  }

  setupViews() {
    const grid = this.querySelector('[data-grid]');
    if (!grid) return;
    const read = (key, fallback) => {
      try {
        return localStorage.getItem(key) || fallback;
      } catch (error) {
        return fallback;
      }
    };
    const write = (key, value) => {
      try {
        localStorage.setItem(key, value);
      } catch (error) {}
    };
    const setSize = (size) => {
      grid.style.setProperty('--nd-cols', size === 'large' ? this.dataset.colsLarge || '3' : this.dataset.cols || '4');
      grid.style.setProperty('--nd-cols-m', size === 'large' ? '1' : this.dataset.colsM || '2');
      this.querySelectorAll('[data-view-size]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.viewSize === size)));
    };
    const setMode = (mode) => {
      grid.classList.toggle('nd-view-cloth', mode === 'cloth');
      this.querySelectorAll('[data-view-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.viewMode === mode)));
    };
    if (this.querySelector('[data-view-size]')) setSize(read('nd-view-size', 'small'));
    if (this.querySelector('[data-view-mode]')) setMode(read('nd-view-mode', 'worn'));
    this.addEventListener('click', (event) => {
      const size = event.target.closest('[data-view-size]');
      const mode = event.target.closest('[data-view-mode]');
      if (size) {
        setSize(size.dataset.viewSize);
        write('nd-view-size', size.dataset.viewSize);
      }
      if (mode) {
        setMode(mode.dataset.viewMode);
        write('nd-view-mode', mode.dataset.viewMode);
      }
    });
  }
}

class NagadaCollection extends NagadaBrowse {}
class NagadaFabrics extends NagadaBrowse {}
if (!customElements.get('nagada-collection')) customElements.define('nagada-collection', NagadaCollection);
if (!customElements.get('nagada-fabrics')) customElements.define('nagada-fabrics', NagadaFabrics);


/*
 * Several photographs in turn (Large photograph with Photograph blocks).
 * Fades to the next photograph every few seconds; pauses while pointed at, while
 * focused, while the tab is hidden, while a block is selected in the theme editor,
 * and for visitors who ask for less motion. Dots, a pause button and swipes move it by hand.
 */
class NagadaSlides extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    if (this.slides.length < 2) return;
    const hero = this.closest('.nd-hero') || this;
    this.captions = [...hero.querySelectorAll('[data-caption]')];
    this.dots = [...this.querySelectorAll('[data-go]')];
    this.pauseButton = this.querySelector('[data-pause]');
    this.counter = this.querySelector('[data-count]');
    this.index = 0;
    this.interval = Number(this.dataset.interval) || 6000;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.stopped = !this.hasAttribute('data-autoplay') || reduced;
    this.held = false;
    this.editing = false;
    if (this.pauseButton && reduced) this.pauseButton.hidden = true;
    this.prepare(1);

    this.dots.forEach((dot) =>
      dot.addEventListener('click', () => {
        this.show(Number(dot.dataset.go));
        this.restart();
      })
    );
    if (this.pauseButton) {
      this.pauseButton.addEventListener('click', () => {
        this.stopped = !this.stopped;
        this.pauseButton.textContent = this.stopped ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel;
        this.restart();
      });
    }
    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.held = true;
      this.restart();
    });
    this.addEventListener('pointerleave', () => {
      this.held = false;
      this.restart();
    });
    this.addEventListener('focusin', () => {
      this.held = true;
      this.restart();
    });
    this.addEventListener('focusout', (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.held = false;
      this.restart();
    });
    this.addEventListener('pointerdown', (event) => {
      this.swipe = { x: event.clientX, y: event.clientY };
    });
    this.addEventListener('pointerup', (event) => {
      if (!this.swipe) return;
      const dx = event.clientX - this.swipe.x;
      const dy = event.clientY - this.swipe.y;
      this.swipe = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      this.show(this.index + (dx < 0 ? 1 : -1));
      this.restart();
    });
    this.addEventListener('shopify:block:select', (event) => {
      const i = this.slides.indexOf(event.target.closest('[data-slide]'));
      if (i < 0) return;
      this.editing = true;
      this.show(i);
      this.restart();
    });
    this.addEventListener('shopify:block:deselect', () => {
      this.editing = false;
      this.restart();
    });
    this.onVisibility = () => this.restart();
    document.addEventListener('visibilitychange', this.onVisibility);
    this.restart();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  // Shows the photograph after the current one (still transparent), so it loads before its turn.
  prepare(step) {
    this.slides.forEach((slide) => slide.classList.remove('is-next'));
    const next = this.slides[(this.index + step) % this.slides.length];
    if (!next.classList.contains('is-active')) next.classList.add('is-next');
  }

  show(target) {
    const count = this.slides.length;
    const index = ((target % count) + count) % count;
    if (index === this.index) return;
    const current = this.slides[this.index];
    const next = this.slides[index];
    this.slides.forEach((slide) => slide.classList.remove('is-prev'));
    next.classList.add('is-next');
    void next.offsetWidth;
    current.classList.remove('is-active');
    current.classList.add('is-prev');
    current.setAttribute('aria-hidden', 'true');
    next.classList.remove('is-next');
    next.classList.add('is-active');
    next.removeAttribute('aria-hidden');
    clearTimeout(this.cleanup);
    this.cleanup = setTimeout(() => current.classList.remove('is-prev'), 1300);
    this.dots.forEach((dot, i) => (i === index ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current')));
    this.captions.forEach((caption, i) => (caption.hidden = i !== index));
    if (this.counter) this.counter.textContent = `${index + 1} / ${count}`;
    this.index = index;
    this.prepare(1);
  }

  restart() {
    clearTimeout(this.timer);
    if (this.stopped || this.held || this.editing || document.hidden) return;
    this.timer = setTimeout(() => {
      this.show(this.index + 1);
      this.restart();
    }, this.interval);
  }
}

if (!customElements.get('nagada-slides')) customElements.define('nagada-slides', NagadaSlides);

/*
 * Textile library: tapping a cloth opens it in a reading view with its photograph
 * and story; Previous and Next (or the arrow keys) step through the others.
 */
class NagadaTextiles extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.entries = [...this.dialog.querySelectorAll('[data-entry]')];
    this.count = this.dialog.querySelector('[data-reader-count]');
    this.querySelectorAll('[data-open]').forEach((tile) =>
      tile.addEventListener('click', () => {
        this.opener = tile;
        this.open(Number(tile.dataset.open));
      })
    );
    this.dialog.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
    this.dialog.querySelectorAll('[data-step]').forEach((button) =>
      button.addEventListener('click', () => this.go(this.index + Number(button.dataset.step)))
    );
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.go(this.index + 1);
      if (event.key === 'ArrowLeft') this.go(this.index - 1);
    });
    this.dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('nd-menu-open');
      if (this.opener) this.opener.focus();
    });
    this.addEventListener('shopify:block:select', (event) => {
      const item = event.target.closest('.nd-textiles__item');
      if (item && this.dialog.open) this.go([...this.querySelectorAll('.nd-textiles__item')].indexOf(item));
    });
  }

  open(index) {
    this.go(index);
    if (!this.dialog.open) this.dialog.showModal();
    document.documentElement.classList.add('nd-menu-open');
  }

  go(target) {
    const total = this.entries.length;
    this.index = ((target % total) + total) % total;
    this.entries.forEach((entry, i) => (entry.hidden = i !== this.index));
    if (this.count) this.count.textContent = `${this.index + 1} / ${total}`;
    this.dialog.scrollTop = 0;
  }
}

if (!customElements.get('nagada-textiles')) customElements.define('nagada-textiles', NagadaTextiles);

/*
 * Fit to the first screen: a large photograph (or row of them) that opens a page
 * is made no taller than the screen space left under the header and the words
 * above it, so the opening of every page is seen whole. Further down the page,
 * photographs keep their designed height.
 */
(() => {
  const fit = () => {
    document.querySelectorAll('[data-nd-fit]').forEach((block) => {
      block.style.removeProperty('--nd-fit-h');
      if (window.innerWidth < 750) return;
      const target = block.matches('.nd-hero__media') ? block : block.querySelector('.nd-frame:not(.nd-frame--tile)');
      if (!target) return;
      const box = target.getBoundingClientRect();
      const top = box.top + window.scrollY;
      if (top > window.innerHeight * 0.6) return;
      const room = window.innerHeight - top - 24;
      if (box.height > room) block.style.setProperty('--nd-fit-h', `${Math.round(Math.max(280, room))}px`);
    });
  };
  let frame;
  const later = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(fit);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fit);
  else fit();
  window.addEventListener('resize', later);
  window.addEventListener('load', later);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(later);
  document.addEventListener('shopify:section:load', later);
})();

/* <nagada-shopper>: the personal shopper panel, opened by any [data-shopper-open] link (the header's, the corner button). */
class NagadaShopper extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.onDocumentClick = (event) => {
      const opener = event.target.closest('[data-shopper-open]');
      if (!opener || event.metaKey || event.ctrlKey) return;
      event.preventDefault();
      this.open();
    };
    document.addEventListener('click', this.onDocumentClick);

    this.querySelector('[data-shopper-close]').addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => document.documentElement.classList.remove('nd-menu-open'));
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  open() {
    const drawer = document.querySelector('.nd-header__menu[open]');
    if (drawer) drawer.open = false;
    if (!this.dialog.open) this.dialog.showModal();
    // Closing the drawer releases its scroll lock a moment later; take it back after that.
    document.documentElement.classList.add('nd-menu-open');
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (this.dialog.open) document.documentElement.classList.add('nd-menu-open');
    }));
  }
}

if (!customElements.get('nagada-shopper')) customElements.define('nagada-shopper', NagadaShopper);
